import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  EventCard,
  EventHomeTable,
} from "@/pages/event_management/event_home/Event_Home";
import {
  selectAllEvents,
  fetchEventByAny,
  selectFetchedEvents,
  selectEventLoading,
} from "@/store/redux/slices/event/event_slice";
import { getOrganizationId } from "@/utils/token_functions";
import { calculateEventCompletionPercentage } from "@/utils/event_utils";
import { isScheduleExpired } from "@/utils/calendar_utils";

const OverviewEvents = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [, setSelectedEvents] = useState([]);
  const rawEvents = useSelector(selectAllEvents);
  const fetchedEventsByOrg = useSelector(selectFetchedEvents);
  const isLoading = useSelector(selectEventLoading);

  // Use the fetched events by organization when available
  const events = useMemo(
    () =>
      fetchedEventsByOrg?.length > 0 ? fetchedEventsByOrg : rawEvents || [],
    [fetchedEventsByOrg, rawEvents]
  );

  const [formattedEvents, setFormattedEvents] = useState([]);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  useEffect(() => {
    const organizationID = getOrganizationId();

    const fetchEvents = async () => {
      try {
        await dispatch(
          fetchEventByAny({
            key: "organizationID",
            value: organizationID,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setHasAttemptedFetch(true);
      }
    };

    fetchEvents();
  }, [dispatch]);

  useEffect(() => {
    // Transform events to match expected format with correct calculations
    if (events && events.length > 0) {
      const transformed = events.map((event) => {
        // Calculate percentage dynamically
        const percentage = calculateEventCompletionPercentage(event);

        // Local end-of-day + endTime (same as SR)
        const currentDate = new Date();
        const isExpired = isScheduleExpired(event.schedule, currentDate);

        // Parse the createdAt date properly
        const createdAtDate = new Date(event.createdAt);

        return {
          ...event, // Include all original event properties
          key: event._id,
          percentage,
          progress: percentage,
          id: event.id || event._id,
          title: event.name,
          publishDate: createdAtDate.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          }),
          createdAt: createdAtDate,
          duration: {
            startDate: event.schedule?.startDate || "",
            startTime: event.schedule?.startTime || "",
            endDate: event.schedule?.endDate || "",
            endTime: event.schedule?.endTime || "",
          },
          locations: event.location || [],
          type: event.type || "Unknown",
          status: isExpired ? "Expired" : "On Going",
          isExpired,
          endDate: event.schedule?.endDate || "",
        };
      });

      // Sort by created date (most recent first) and take top 4
      const sortedEvents = transformed
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, 4);

      setFormattedEvents(sortedEvents);
    }
  }, [events]);

  const handleDelete = () => {
    // Implement delete logic if needed for overview
  };

  const handleModify = (eventId) => {
    navigate(`/dashboard/events/edit/${eventId}`);
  };

  // Determine what to display
  const renderContent = () => {
    if (isLoading && !hasAttemptedFetch) {
      return <div className="text-center py-4">Loading events...</div>;
    }

    if (!isLoading && formattedEvents.length === 0) {
      return (
        <div className="text-center py-4">
          No events found. Add events to see them here.
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 gap-4 mt-6 mb-6 md:grid-cols-2 lg:grid-cols-1 lg:hidden">
          {formattedEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onDelete={handleDelete}
              onModify={handleModify}
            />
          ))}
        </div>

        <div className="hidden lg:block">
          <EventHomeTable
            setSelectedEvents={setSelectedEvents}
            currentPage={1}
            pageSize={4}
            total={formattedEvents.length}
            onDelete={handleDelete}
            onModify={handleModify}
            filteredEvents={formattedEvents}
            activeFilter="all"
            simplified={true}
            skipFetch={true}
          />
        </div>
      </>
    );
  };

  return <div>{renderContent()}</div>;
};

export default OverviewEvents;
